/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { FormGroup, Stack } from "@mui/material";
import { DateTimeInput } from "components/Common/DateTimeInput";
import { LabeledSwitch } from "components/Common/LabeledSwitch";
import { MultiObjectSelector } from "components/Common/MultiObjectSelector";
import { NodeSelector } from "components/Common/NodeSelector";
import { CountTextField } from "components/Tasks/CountTextField";
import { useAwvDataContext } from "context/AwvDataProvider";
import { useTaskRegisterDataContext } from "context/TaskRegisterDataProvider";
import { FC, useMemo, useRef, useState } from "react";
import { PanelContentsContainer } from "styles/TaskRegister";
import { validateFromTo } from "utils/DateTime";
import { ConditionalTextField } from "components/Common/ConditionalTextField";
import { Const } from "const";
import { useTranslation } from "react-i18next";
import { TaskControlMode } from "types";

export interface MultipleChargeJobsetInputItem {
  jobsetId?: string;
  jobsetName: string | undefined;
  startNodeId: number | undefined;
  endNodeId: number | undefined;
  startDate: Date | undefined;
  endDate: Date | undefined;
  memo: string | undefined;
  frameTypeAndNumbers: string[];
  chargerIds: string[];
  moveToStart: boolean;
  resetJobRequestedEndDatetime: boolean;
  chargeOutsideDelivery: boolean;
  versionId?: string;
}
interface MultipleChargeSettingPanelProps {
  jobset: MultipleChargeJobsetInputItem;
  mode: TaskControlMode;
  /**
   * Callback for contents edited
   *
   * @type {?(job: MultipleChargeJobsetInputItem) => void}
   */
  onEdit?: (jobset: MultipleChargeJobsetInputItem) => void;
}

export const MultipleChargeSettingPanel: FC<
  MultipleChargeSettingPanelProps
> = ({ jobset, mode, onEdit }) => {
  const { t } = useTranslation();
  const [jobSetName, setJobSetName] = useState(jobset?.jobsetName);
  const [memo, setMemo] = useState(jobset?.memo);
  const [valid, setValid] = useState(
    validateFromTo(
      jobset?.startDate ?? new Date(),
      jobset?.endDate ?? new Date()
    )
  );

  const { awvData } = useAwvDataContext();
  const { chargers } = useTaskRegisterDataContext();
  const awvItems = useMemo(() => {
    return (
      awvData.awvInfos?.map((awv) => {
        return { id: awv.frameTypeAndNumber ?? "", label: awv.nameId ?? "" };
      }) ?? []
    );
  }, [awvData.awvInfos]);
  const chargerItems = useMemo(() => {
    return (
      chargers?.map((charger) => {
        return { id: charger.id, label: charger.nameId };
      }) ?? []
    );
  }, [chargers]);
  const jobsetRef = useRef(jobset);

  return (
    <PanelContentsContainer>
      <Stack spacing={1}>
        <ConditionalTextField
          value={jobSetName}
          label={t("AWVUI-MSG212")}
          acceptPattern={Const.regExp.asciiPattern}
          maxLength={50}
          onChanged={(text) => {
            setJobSetName(text);
            jobsetRef.current.jobsetName = text;
            onEdit?.(jobsetRef.current);
          }}
          textFieldProps={{
            error: (jobSetName?.length ?? 0) === 0,
            sx: { backgroundColor: "white" },
          }}
        />
        <NodeSelector
          label={t("AWVUI-MSG213")}
          nodeId={jobset?.startNodeId}
          onSelected={(point) => {
            jobsetRef.current.startNodeId = point.nodeId;
            onEdit?.(jobsetRef.current);
          }}
        />
        <NodeSelector
          label={t("AWVUI-MSG214")}
          nodeId={jobset?.endNodeId}
          onSelected={(point) => {
            jobsetRef.current.endNodeId = point.nodeId;
            onEdit?.(jobsetRef.current);
          }}
        />
        <DateTimeInput
          label={t("AWVUI-MSG215")}
          date={jobset?.startDate}
          onDateTimeSelected={(date) => {
            if (date != null) {
              setValid(validateFromTo(date, jobsetRef.current.endDate));
              jobsetRef.current.startDate = date;
              onEdit?.(jobsetRef.current);
            }
          }}
          valid={valid[0]}
        />
        <DateTimeInput
          label={t("AWVUI-MSG216")}
          date={jobset?.endDate}
          onDateTimeSelected={(date) => {
            if (date != null) {
              setValid(validateFromTo(jobsetRef.current.startDate, date));
              jobsetRef.current.endDate = date;
              onEdit?.(jobsetRef.current);
            }
          }}
          valid={valid[1]}
        />
        <CountTextField
          label={t("AWVUI-MSG41")}
          value={memo ?? ""}
          maxLength={150}
          onChanged={(text) => {
            setMemo(text);
            jobsetRef.current.memo = text;
            onEdit?.(jobsetRef.current);
          }}
        />
        <MultiObjectSelector
          label={t("AWVUI-MSG217")}
          sources={awvItems}
          selected={jobsetRef.current.frameTypeAndNumbers}
          onSelected={(selected) => {
            jobsetRef.current.frameTypeAndNumbers = selected;
            onEdit?.(jobsetRef.current);
          }}
        />
        <MultiObjectSelector
          label={t("AWVUI-MSG218")}
          sources={chargerItems}
          selected={jobsetRef.current.chargerIds}
          onSelected={(selected) => {
            jobsetRef.current.chargerIds = selected;
            onEdit?.(jobsetRef.current);
          }}
        />
        <FormGroup>
          {mode !== "edit" && (
            <LabeledSwitch
              checked={jobsetRef.current.moveToStart}
              onChange={(event) => {
                jobsetRef.current.moveToStart = event.target.checked;
                onEdit?.(jobsetRef.current);
              }}
              sx={{ m: 1 }}
              label={t("AWVUI-MSG167")}
            />
          )}
          <LabeledSwitch
            checked={jobsetRef.current.resetJobRequestedEndDatetime}
            onChange={(event) => {
              jobsetRef.current.resetJobRequestedEndDatetime =
                event.target.checked;
              onEdit?.(jobsetRef.current);
            }}
            sx={{ m: 1 }}
            label={t("AWVUI-MSG219")}
          />
          <LabeledSwitch
            checked={jobsetRef.current.chargeOutsideDelivery}
            onChange={(event) => {
              jobsetRef.current.chargeOutsideDelivery = event.target.checked;
              onEdit?.(jobsetRef.current);
            }}
            sx={{ m: 1 }}
            label={t("AWVUI-MSG220")}
          />
        </FormGroup>
      </Stack>
    </PanelContentsContainer>
  );
};
